import * as React from 'react';
import { StylesConfig, DropdownIndicatorProps, components } from 'react-select';
import AsyncSelect from 'react-select/async';

import { SchoolOption, SchoolNameAutocompleteProps } from './types';

/**
 * Custom dropdown indicator matching existing select arrow style (step LP)
 * Uses filled triangle arrow to match arrow.svg (#525252)
 * Size: 9px (matching background-size: 9px auto in step.scss)
 */
const CustomDropdownIndicator = (props: DropdownIndicatorProps<SchoolOption, false>) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg width="9" height="8" viewBox="0 0 14 12" style={{ marginRight: '8px' }}>
        <path
          d="M6.136,1.481a1,1,0,0,1,1.728,0L13.123,10.5a1,1,0,0,1-.864,1.5H1.741a1,1,0,0,1-.864-1.5Z"
          transform="translate(14 12) rotate(180)"
          fill="#525252"
        />
      </svg>
    </components.DropdownIndicator>
  );
};

/**
 * 学校名予測入力コンポーネント (Step LP用)
 */
const SchoolNameAutocomplete: React.FC<SchoolNameAutocompleteProps> = ({
  schoolType,
  value,
  onChange,
  isDisabled = false,
  hasError = false
}) => {
  /**
   * ProfileのschoolTypeをSchoolMasterのschoolTypeに変換
   */
  const convertSchoolType = (profileSchoolType: string): string => {
    const mapping: { [key: string]: string } = {
      '1': '0', // university
      '2': '1', // junior_college
      '3': '3' // agricultural_college
    };
    return mapping[profileSchoolType] || profileSchoolType;
  };

  const loadOptions = async (inputValue: string): Promise<SchoolOption[]> => {
    if (!inputValue || inputValue.length < 2) {
      return [];
    }

    try {
      const schoolMasterType = convertSchoolType(schoolType);
      const response = await fetch(
        `/api/school_masters/search?query=${encodeURIComponent(inputValue)}&school_type=${schoolMasterType}`
      );

      if (!response.ok) {
        console.error('School search API error:', response.statusText);
        return [];
      }

      const data: SchoolOption[] = await response.json();
      return data;
    } catch (error) {
      console.error('School search error:', error);
      return [];
    }
  };

  // Styles matching existing select in step.scss and babysteps.css.scss
  // Existing select height calculation:
  // - border: 2px × 2 = 4px
  // - padding: 15px × 2 = 30px
  // - content (font-size: 14px, line-height ~1.2) ≈ 17px
  // - Total: ~51px
  const customStyles: StylesConfig<SchoolOption> = {
    control: (base, state) => ({
      ...base,
      border: hasError ? '2px solid #e74c3c' : '2px solid #059F19',
      borderRadius: '7px',
      boxShadow: hasError ? '0 0 0 1px #e74c3c' : state.isFocused ? '0 0 0 1px #059F19' : 'none',
      '&:hover': {
        borderColor: hasError ? '#e74c3c' : '#059F19'
      },
      minHeight: '0',
      fontSize: '14px', // Match babysteps.css.scss
      backgroundColor: '#FFF'
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '15px' // Match step.scss select padding: 15px (all directions)
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      lineHeight: 1.15 // font-size 14px × 1.15 ≈ 16.1px (matches existing select)
    }),
    placeholder: (base) => ({
      ...base,
      color: '#999',
      margin: 0,
      lineHeight: 1.15
    }),
    singleValue: (base) => ({
      ...base,
      margin: 0,
      lineHeight: 1.15
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#f0f8f0' : 'white',
      color: '#333',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#e0f0e0'
      }
    }),
    menu: (base) => ({
      ...base,
      zIndex: 100
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '0' // No padding - arrow at right edge like background-position: 97%
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '0'
    }),
    indicatorsContainer: (base) => ({
      ...base,
      alignSelf: 'stretch',
      alignItems: 'center'
    })
  };

  return (
    <AsyncSelect<SchoolOption>
      cacheOptions
      defaultOptions={false}
      loadOptions={loadOptions}
      onChange={onChange}
      value={value}
      isDisabled={isDisabled}
      placeholder="例) 〇〇大学"
      noOptionsMessage={({ inputValue }) => {
        if (!inputValue || inputValue.length < 2) {
          return '2文字以上入力してください';
        }
        return '該当する学校が見つかりませんでした';
      }}
      loadingMessage={() => '検索中...'}
      styles={customStyles}
      components={{ DropdownIndicator: CustomDropdownIndicator }}
      isClearable
    />
  );
};

export default SchoolNameAutocomplete;
